import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Loader2, Search, X, User } from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../components/ui/select";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const CartaoModal = ({ open, cartao, colunas, onClose, onSaved }) => {
	const [title, setTitle] = useState("");
	const [description, setDescription] = useState("");
	const [value, setValue] = useState("");
	const [dueDate, setDueDate] = useState("");
	const [columnId, setColumnId] = useState(null);
	const [userId, setUserId] = useState(null);
	const [contato, setContato] = useState(null);
	const [usuarios, setUsuarios] = useState([]);
	const [salvando, setSalvando] = useState(false);

	// Busca de contato: o cartão é de alguém, e é desse alguém que sai o
	// telefone para o aviso da coluna.
	const [busca, setBusca] = useState("");
	const [resultados, setResultados] = useState([]);
	const [buscando, setBuscando] = useState(false);

	const editando = !!cartao?.id;

	useEffect(() => {
		if (!open) return;

		setTitle(cartao?.title || "");
		setDescription(cartao?.description || "");
		setValue(cartao?.value ?? "");
		setDueDate(cartao?.dueDate ? String(cartao.dueDate).slice(0, 10) : "");
		setColumnId(cartao?.columnId || colunas?.[0]?.id || null);
		setUserId(cartao?.userId || null);
		setContato(cartao?.contact || null);
		setBusca("");
		setResultados([]);
	}, [open, cartao, colunas]);

	useEffect(() => {
		if (!open) return;

		(async () => {
			try {
				const { data } = await api.get("/users/");
				setUsuarios(data.users || []);
			} catch {
				// Sem a lista, o campo de responsável fica vazio — o cartão salva
				// do mesmo jeito.
				setUsuarios([]);
			}
		})();
	}, [open]);

	useEffect(() => {
		if (!busca.trim()) {
			setResultados([]);
			return undefined;
		}

		// Espera a pessoa parar de digitar: sem isso é uma requisição por tecla.
		const id = setTimeout(async () => {
			setBuscando(true);
			try {
				const { data } = await api.get("/contacts/", {
					params: { searchParam: busca },
				});
				setResultados(data.contacts || []);
			} catch (err) {
				toastError(err);
			}
			setBuscando(false);
		}, 400);

		return () => clearTimeout(id);
	}, [busca]);

	const salvar = async () => {
		if (!columnId) {
			toast.error("Escolha a coluna.");
			return;
		}

		if (!title.trim() && !contato) {
			toast.error("Dê um título ao cartão ou escolha o cliente.");
			return;
		}

		setSalvando(true);
		try {
			const corpo = {
				columnId,
				title: title.trim(),
				description,
				value: value === "" ? null : Number(value),
				dueDate: dueDate || null,
				userId,
				contactId: contato?.id || null,
			};

			if (editando) {
				await api.put(`/kanban/cards/${cartao.id}`, corpo);
			} else {
				await api.post("/kanban/cards", corpo);
			}

			toast.success(editando ? "Cartão salvo." : "Cartão criado.");
			onSaved?.();
			onClose();
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	const colunaEscolhida = colunas?.find((c) => c.id === columnId);

	return (
		<Dialog open={open} onOpenChange={(o) => !o && onClose()}>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>{editando ? "Editar cartão" : "Novo cartão"}</DialogTitle>
					<DialogDescription>
						O trabalho de um cliente: o pedido, a venda, a entrega.
					</DialogDescription>
				</DialogHeader>

				<div className="max-h-[60vh] space-y-4 overflow-y-auto pr-1">
					<div className="space-y-1.5">
						<Label>Cliente</Label>

						{contato ? (
							<div className="flex items-center gap-2 rounded-lg border bg-muted/40 p-2.5">
								<User className="h-4 w-4 shrink-0 text-muted-foreground" />
								<span className="min-w-0 flex-1 truncate text-sm">
									{contato.name || contato.number}
									{contato.name && (
										<span className="text-muted-foreground">
											{" "}
											· {contato.number}
										</span>
									)}
								</span>
								<Button
									type="button"
									variant="ghost"
									size="icon"
									className="h-8 w-8 shrink-0"
									aria-label="Tirar cliente"
									onClick={() => setContato(null)}
								>
									<X className="h-4 w-4" />
								</Button>
							</div>
						) : (
							<>
								<div className="relative">
									<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
									<Input
										className="pl-8"
										value={busca}
										onChange={(e) => setBusca(e.target.value)}
										placeholder="Buscar por nome ou telefone"
									/>
								</div>
								{buscando && (
									<p className="text-xs text-muted-foreground">Buscando...</p>
								)}
								{resultados.length > 0 && (
									<div className="max-h-40 overflow-y-auto rounded-lg border">
										{resultados.map((c) => (
											<button
												key={c.id}
												type="button"
												onClick={() => {
													setContato(c);
													setBusca("");
													setResultados([]);
													if (!title.trim()) setTitle(c.name || c.number);
												}}
												className="block w-full px-3 py-2 text-left text-sm hover:bg-muted"
											>
												<span className="font-medium">{c.name || c.number}</span>
												{c.name && (
													<span className="text-muted-foreground">
														{" "}
														· {c.number}
													</span>
												)}
											</button>
										))}
									</div>
								)}
								<p className="text-xs leading-relaxed text-muted-foreground">
									Sem cliente o cartão continua valendo, mas nenhum aviso de
									coluna sai — não há para quem mandar.
								</p>
							</>
						)}
					</div>

					<div className="space-y-1.5">
						<Label htmlFor="cartao-titulo">Título</Label>
						<Input
							id="cartao-titulo"
							value={title}
							onChange={(e) => setTitle(e.target.value)}
							placeholder="Ex.: 200 camisetas — pedido 4312"
						/>
					</div>

					<div className="space-y-1.5">
						<Label>Coluna</Label>
						<Select
							value={columnId ? String(columnId) : undefined}
							onValueChange={(v) => setColumnId(Number(v))}
						>
							<SelectTrigger>
								<SelectValue placeholder="Escolha a coluna" />
							</SelectTrigger>
							<SelectContent>
								{(colunas || []).map((c) => (
									<SelectItem key={c.id} value={String(c.id)}>
										{c.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						{!editando && colunaEscolhida?.triggerMessage && contato && (
							<p className="text-xs leading-relaxed text-primary">
								Ao criar aqui, o cliente recebe: "{colunaEscolhida.triggerMessage}"
							</p>
						)}
					</div>

					<div className="grid grid-cols-2 gap-3">
						<div className="space-y-1.5">
							<Label htmlFor="cartao-valor">
								Valor{" "}
								<span className="font-normal text-muted-foreground">
									(opcional)
								</span>
							</Label>
							<Input
								id="cartao-valor"
								type="number"
								step="0.01"
								value={value}
								onChange={(e) => setValue(e.target.value)}
								placeholder="0,00"
							/>
						</div>
						<div className="space-y-1.5">
							<Label htmlFor="cartao-prazo">
								Prazo{" "}
								<span className="font-normal text-muted-foreground">
									(opcional)
								</span>
							</Label>
							<Input
								id="cartao-prazo"
								type="date"
								value={dueDate}
								onChange={(e) => setDueDate(e.target.value)}
							/>
						</div>
					</div>

					<div className="space-y-1.5">
						<Label>Responsável</Label>
						<Select
							value={userId ? String(userId) : "nenhum"}
							onValueChange={(v) => setUserId(v === "nenhum" ? null : Number(v))}
						>
							<SelectTrigger>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="nenhum">Sem responsável</SelectItem>
								{usuarios.map((u) => (
									<SelectItem key={u.id} value={String(u.id)}>
										{u.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					<div className="space-y-1.5">
						<Label htmlFor="cartao-obs">Observações</Label>
						<Textarea
							id="cartao-obs"
							rows={3}
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							placeholder="O que a equipe precisa saber sobre este trabalho."
						/>
					</div>
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={onClose} disabled={salvando}>
						Cancelar
					</Button>
					<Button onClick={salvar} disabled={salvando}>
						{salvando && <Loader2 className="h-4 w-4 animate-spin" />}
						{editando ? "Salvar" : "Criar cartão"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default CartaoModal;
